import { createSlice, isPending, isFulfilled, isRejected, PayloadAction } from '@reduxjs/toolkit'
import type { GetKongLocationAllResponseType } from 'features/Location/@types/location'
import type { IncentiveItemType, GetIncentiveListResponseType } from '../../services/incentive'
import {
  SLICE_NAME,
  actFetchIncentiveDataList,
  actFetchIncentiveDetail,
  actFetchLocationList,
  actSubmitIncentiveData,
  IncentiveDetailDataType,
} from './thunk'

export type GetMasterIncentiveParamsType = {
  search: string
  activity: string
  type: string
  status: string
  page_size: number
  page_index: number
  cursor?: string
}

export type MasterIncentiveInitialStateType = {
  incentiveDataList: IncentiveItemType[]
  queryParams: GetMasterIncentiveParamsType
  isLoading: boolean
  numberOfElements: number
  pagination: GetIncentiveListResponseType['pagination']
  incentiveDetailData: Nullable<IncentiveDetailDataType>
  queryParamsForLocationList: {
    type?: string
    page: number
    limit: number
    name?: string
  }
  locationList: GetKongLocationAllResponseType['location']
}

const initialState: MasterIncentiveInitialStateType = {
  incentiveDataList: [],
  queryParams: {
    search: '',
    activity: '',
    type: '',
    status: '',
    page_size: 10,
    page_index: 1,
    cursor: '',
  },
  isLoading: false,
  numberOfElements: 0,
  pagination: {
    next_cursor: '',
    prev_cursor: '',
  },
  incentiveDetailData: null,
  queryParamsForLocationList: {
    page: 0,
    limit: 10,
    type: 'all',
  },
  locationList: [],
}

const masterIncentiveSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    setQueryParams: (state, action: PayloadAction<Partial<GetMasterIncentiveParamsType>>) => {
      state.queryParams = { ...state.queryParams, ...action.payload }
    },
    setQueryParamsForLocationList: (
      state,
      action: PayloadAction<Partial<MasterIncentiveInitialStateType['queryParamsForLocationList']>>,
    ) => {
      state.queryParamsForLocationList = { ...state.queryParamsForLocationList, ...action.payload }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(actFetchIncentiveDataList.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.incentiveDataList = payload.data
        state.numberOfElements = payload.data.length
        state.pagination = payload.pagination
      })
      .addCase(actFetchIncentiveDetail.fulfilled, (state, { payload }) => {
        state.incentiveDetailData = payload
      })
      .addCase(actFetchLocationList.fulfilled, (state, { payload }) => {
        state.locationList = payload
      })
      .addMatcher(
        isPending(actFetchIncentiveDataList, actFetchIncentiveDetail, actSubmitIncentiveData),
        (state) => {
          state.isLoading = true
        },
      )
      .addMatcher(
        isFulfilled(actFetchIncentiveDataList, actFetchIncentiveDetail, actSubmitIncentiveData),
        (state) => {
          state.isLoading = false
        },
      )
      .addMatcher(
        isRejected(actFetchIncentiveDataList, actFetchIncentiveDetail, actSubmitIncentiveData),
        (state) => {
          state.isLoading = false
        },
      )
  },
})

export const { reset, setQueryParams, setQueryParamsForLocationList } = masterIncentiveSlice.actions

export default masterIncentiveSlice.reducer
